import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton } from '@mui/material';
import { iconButtonTouchTargetSx } from '../../utils/a11y.utils';
import stepperStyles from './Stepper.styles';
import StepperContext from '../Context/StepperContext';

//Customhook
import { useTranslation } from '../../hooks/useTranslation';

function StepperBar(props) {
	const { t, translations } = useTranslation();
	const location = useLocation();
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	const { currentStep } = useContext(StepperContext);
	const showBox =
		/^\/accounts(\/.*)?$/.test(location.pathname) ||
		/^\/home\/?$/.test(location.pathname) ||
		/^\/NoAccountFound(\/.*)?$/.test(location.pathname);

	// To prevent flicker
	if (!translations || !translations.common || !translations.common.labels) {
		return null;
	}

	// In isFlowChange: 2 steps (Register → Approve Consents)
	// In normal / renewal flow: 3 steps
	const isTwoStepFlow = global.isFlowChange === 'true';
	const totalSteps = isTwoStepFlow ? 2 : 3;

	// Check if on consent page
	const isOnConsentPage = location.pathname.includes('/payment-consent');
	const isOnConsentRenewPage = location.pathname.includes('/consent-renew');

	// Calculate progress
	let progressPercentage;

	// Allow overriding progress percentage via props
	if (props.progressPercentage !== undefined) {
		progressPercentage = props.progressPercentage;
	} else if (isTwoStepFlow || global.renewConsentId) {
		// In isFlowChange flow, use pathname-based logic
		if (isOnConsentPage || isOnConsentRenewPage) {
			// On consent page, show 90%
			progressPercentage = 90;
		} else {
			// On login or discovery page, show 50%
			progressPercentage = 50;
		}
	} else {
		// Normal flow calculation, cap at 90% (never fill completely)
		progressPercentage = Math.min(((currentStep + 1) / totalSteps) * 100, 90);
	}

	const displayStep =
		isTwoStepFlow || global.renewConsentId
			? isOnConsentPage || isOnConsentRenewPage
				? 2
				: 1
			: currentStep + 1;

	const isDark =
		props.customDetails?.darkTheme === true || theme.palette.mode === 'dark';
	const loginPage = props.login;

	// Dynamic padding calculation
	const calculatePadding = () => {
		// LSP flow (Verify Accounts) needs more spacing
		if (global.isFlowChange === 'true') {
			return mobileScreen ? '95px' : '100px';
		}
		// Normal login/consent flow
		return mobileScreen ? '95px' : '100px';
	};

	const mobileDiscoveryLayout = !loginPage && mobileScreen && showBox;

	const barWidth = loginPage
		? mobileScreen
			? '98%'
			: '72%'
		: mobileDiscoveryLayout
			? '100%'
			: mobileScreen
				? '95%'
				: '60%';

	return (
		<Box
			component="nav"
			role="navigation"
			aria-label={t('common.labels.progressSteps')}
			sx={{
				...stepperStyles.stepperContainer,
				width: '100%',
				maxWidth: '100%',
				boxSizing: 'border-box',
				paddingTop: loginPage ? undefined : calculatePadding(),
				...(mobileDiscoveryLayout && {
					px: '1.5rem',
					pb: '0.75rem',
				}),
				...(loginPage && {
					position: 'absolute',
					top: '0.5rem',
					left: 0,
					right: 0,
					width: '100%',
					padding: '0 1.25rem !important',
				}),
				backgroundColor: showBox ? theme.palette.primary.light : 'transparent',
				display: 'flex',
				flexDirection: 'column',
				alignItems: mobileDiscoveryLayout ? 'stretch' : 'center',
				gap: '6px',
			}}
		>
			<Box
				sx={{
					width: barWidth,
					maxWidth: '100%',
					minWidth: 0,
					display: 'flex',
					alignItems: 'center',
					gap: '6px',
				}}
			>
				<Typography
					sx={{
						fontSize: '0.8125rem',
						color: isDark ? '#AAAAAA' : '#6B6B6B',
						fontFamily: props.customDetails?.fontFamily,
						lineHeight: 1,
					}}
				>
					{t('common.labels.progressStep', {
						current: displayStep,
						total: totalSteps,
					})}
				</Typography>
				<IconButton
					onClick={props.onInfoClick}
					disabled={!props.onInfoClick}
					aria-label={t('common.buttons.info')}
					sx={{
						p: 0,
						...iconButtonTouchTargetSx,
						'&:hover': props.onInfoClick
							? { backgroundColor: 'transparent' }
							: undefined,
					}}
				>
					<InfoOutlinedIcon
						sx={{
							fontSize: '1rem',
							color: isDark ? '#AAAAAA' : '#6B6B6B',
							cursor: props.onInfoClick ? 'pointer' : 'default',
							'&:hover': props.onInfoClick
								? {
										color:
											props.customDetails?.primaryColor ||
											theme.palette.primary.main,
									}
								: undefined,
						}}
					/>
				</IconButton>
			</Box>
			<Box
				sx={{
					width: barWidth,
					maxWidth: '100%',
					minWidth: 0,
					height: '8px',
					backgroundColor: isDark ? '#2D2D2D' : '#E0E0E0',
					borderRadius: '4px',
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						width: `${progressPercentage}%`,
						height: '100%',
						backgroundColor:
							props.customDetails?.primaryColor || theme.palette.primary.main,
						transition: 'width 0.3s ease-in-out',
						borderRadius: '4px',
					}}
				/>
			</Box>
		</Box>
	);
}

export default StepperBar;
